<!-- app-subdomain-creators/src/App.vue -->

<template>
  <CreatorPlanLimitPrompt
    :open="globalPlanLimitPromptOpen"
    :resource="globalPlanLimitResource"
    @close="globalPlanLimitPromptOpen = false"
  />
  <router-view /> <!-- This renders the component for the current route -->
  <div
    v-if="trackingAvailable && consentOpen"
    class="platforma-tracking-consent"
    role="dialog"
    aria-label="Analytics privacy choices"
  >
    <div>
      <p class="platforma-tracking-consent__title">
        Platforma privacy choices
      </p>
      <p class="platforma-tracking-consent__copy">
        With your permission, Platforma uses analytics and advertising measurement tags. These tags are not loaded until you accept.
      </p>
    </div>
    <div class="platforma-tracking-consent__actions">
      <button
        type="button"
        class="platforma-tracking-consent__secondary"
        aria-label="Decline analytics and advertising tracking"
        @click="setTrackingConsent('declined')"
      >
        Decline
      </button>
      <button
        type="button"
        class="platforma-tracking-consent__primary"
        aria-label="Accept analytics and advertising tracking"
        @click="setTrackingConsent('accepted')"
      >
        Accept
      </button>
    </div>
  </div>
  <button
    v-else-if="trackingAvailable"
    type="button"
    class="platforma-tracking-privacy-choice"
    aria-label="Open analytics privacy choices"
    @click="consentOpen = true"
  >
    Privacy choices
  </button>
  <Transition name="creator-login-splash">
    <div
      v-if="showLoginTransition"
      class="creator-login-transition"
      :class="{ 'creator-login-transition--logout': transitionMode === 'logout' }"
      role="status"
      aria-live="polite"
    >
      <div class="creator-login-transition__mark">
        <img
          src="@/assets/media/images/platformalogostack.png"
          alt="Platforma"
        >
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { onMounted, onUnmounted, provide, ref } from 'vue';
import CreatorPlanLimitPrompt from '@/Components/CreatorPlanLimitPrompt.vue';
import { provideFeatureStore } from '@/stores/featureStore';

provideFeatureStore(); // Provide the feature store globally

const globalPlanLimitPromptOpen = ref(false);
const trackingAvailable = ref(false);
const consentOpen = ref(false);
const trackingConsentKey = 'platforma_platform_tracking_consent_v1';
const handleTrackingReady = (event) => {
  trackingAvailable.value = Boolean(event?.detail?.active);
  if (!trackingAvailable.value) return;
  try { consentOpen.value = !window.localStorage.getItem(trackingConsentKey); }
  catch (_error) { consentOpen.value = true; }
};
const setTrackingConsent = (value) => {
  let previous = '';
  try { previous = window.localStorage.getItem(trackingConsentKey) || ''; } catch (_error) { /* Storage can be unavailable. */ }
  try { window.localStorage.setItem(trackingConsentKey, value); } catch (_error) { /* Storage can be unavailable. */ }
  consentOpen.value = false;
  window.dispatchEvent(new CustomEvent('platforma:platform-tracking-consent-changed'));
  if (value === 'declined' && previous === 'accepted') window.location.reload();
};
const globalPlanLimitResource = ref({});
const handleGlobalPlanLimit = (event) => {
  globalPlanLimitResource.value = event?.detail || {};
  globalPlanLimitPromptOpen.value = true;
};

if (typeof window !== 'undefined') {
  window.addEventListener('creator-plan-limit-reached', handleGlobalPlanLimit);
  window.addEventListener('platforma:platform-tracking-config-ready', handleTrackingReady);
}

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('creator-plan-limit-reached', handleGlobalPlanLimit);
    window.removeEventListener('platforma:platform-tracking-config-ready', handleTrackingReady);
  }
});

const logoutTransitionKey = 'platforma_creator_logout_transition';
const resumeLogoutTransition = (() => {
  if (typeof window === 'undefined' || window.location.pathname !== '/') return false;
  try {
    const shouldResume = window.sessionStorage.getItem(logoutTransitionKey) === '1';
    window.sessionStorage.removeItem(logoutTransitionKey);
    return shouldResume;
  } catch (_error) {
    return false;
  }
})();

const showLoginTransition = ref(resumeLogoutTransition);
const transitionMode = ref(resumeLogoutTransition ? 'logout' : 'login');
const waitForMinimumVisibility = async (startedAt) => {
  const minimumVisibleMs = 2800;
  const remainingMs = Math.max(0, minimumVisibleMs - (Date.now() - startedAt));
  if (remainingMs) await new Promise((resolve) => setTimeout(resolve, remainingMs));
};

provide('creatorLoginTransition', {
  start() {
    transitionMode.value = 'login';
    showLoginTransition.value = true;
    return Date.now();
  },
  async finish(startedAt) {
    await waitForMinimumVisibility(startedAt);
    showLoginTransition.value = false;
  },
  startLogout() {
    transitionMode.value = 'logout';
    showLoginTransition.value = true;
    return Date.now();
  },
  waitForLogout: waitForMinimumVisibility,
  cancel() {
    showLoginTransition.value = false;
  },
});

onMounted(() => {
  if (window.__platformaTrackingAvailable) handleTrackingReady({ detail: { active: true } });
  if (resumeLogoutTransition) {
    window.setTimeout(() => { showLoginTransition.value = false; }, 180);
  }
});
</script>

<style>
.creator-login-transition {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: grid;
  place-items: center;
}

.creator-login-transition__mark {
  display: grid;
  place-items: center;
  width: 190px;
  height: 190px;
  animation: creator-login-breathe 1.8s ease-in-out infinite;
}

.creator-login-transition__mark img {
  display: block;
  width: 156px;
  height: auto;
  opacity: .5;
}

.creator-login-splash-enter-active {
  transition: opacity .22s ease;
}

.creator-login-transition--logout.creator-login-splash-enter-active {
  transition: opacity 1.05s ease-in;
}

.creator-login-splash-leave-active {
  transition: opacity 1.05s ease-out;
}

.creator-login-splash-enter-from,
.creator-login-splash-leave-to {
  opacity: 0;
}

@keyframes creator-login-breathe {
  0%, 100% { transform: scale(.96); }
  50% { transform: scale(1.04); }
}

@media (prefers-reduced-motion: reduce) {
  .creator-login-transition__mark { animation: none; }
  .creator-login-splash-enter-active,
  .creator-login-splash-leave-active { transition: none; }
}

.platforma-tracking-consent {
  position: fixed; right: 1rem; bottom: 1rem; z-index: 2147482000; display: flex; max-width: 42rem;
  align-items: center; gap: 1rem; border: 1px solid rgba(45, 140, 255, .38); border-radius: 1rem;
  background: rgba(8, 15, 27, .97); padding: 1rem; color: #fff; box-shadow: 0 20px 55px rgba(0, 0, 0, .4);
}
.platforma-tracking-consent__title { margin: 0; font-size: .95rem; font-weight: 700; }
.platforma-tracking-consent__copy { margin: .3rem 0 0; color: #aab8cb; font-size: .78rem; line-height: 1.5; }
.platforma-tracking-consent__actions { display: flex; flex: none; gap: .5rem; }
.platforma-tracking-consent button, .platforma-tracking-privacy-choice { border-radius: .65rem; padding: .65rem .9rem; font-size: .78rem; font-weight: 700; cursor: pointer; }
.platforma-tracking-consent__primary { border: 1px solid #2d8cff; background: #2d8cff; color: #fff; }
.platforma-tracking-consent__secondary { border: 1px solid #435066; background: transparent; color: #dbe5f3; }
.platforma-tracking-privacy-choice { position: fixed; left: .75rem; bottom: .75rem; z-index: 2147481000; border: 1px solid #d1d9e5; background: rgba(255,255,255,.94); color: #344054; box-shadow: 0 8px 24px rgba(0,0,0,.12); }
@media (max-width: 640px) { .platforma-tracking-consent { left: .75rem; right: .75rem; bottom: .75rem; display: block; } .platforma-tracking-consent__actions { margin-top: .85rem; justify-content: flex-end; } }
</style>
